import React, { useState } from 'react';
import './ClinicalExposureHeatmap.css';

interface ClinicalExposureHeatmapProps {
  residentId: number;
  globalTimeframe?: '1 week' | '1 month' | '2 months' | '6 months';
}

// Department totals by field (mock - represents total cases in department)
const departmentTotals: { [key: string]: number } = {
  'Cardiology': 320,
  'Pulmonology': 280,
  'Gastroenterology': 240,
  'Nephrology': 200,
  'Neurology': 180,
  'Endocrinology': 150,
  'Infectious Disease': 300,
  'Hematology': 120,
};

// Average across all residents by field (for comparison)
const residentAverages: { [key: string]: { all: number; complex: number } } = {
  'Cardiology': { all: 50, complex: 13 },
  'Pulmonology': { all: 43, complex: 11 },
  'Gastroenterology': { all: 38, complex: 9 },
  'Nephrology': { all: 33, complex: 11 },
  'Neurology': { all: 27, complex: 6 },
  'Endocrinology': { all: 22, complex: 5 },
  'Infectious Disease': { all: 42, complex: 13 },
  'Hematology': { all: 19, complex: 4 },
};

// Mock data for clinical exposure by medical field
const exposureData: { [key: number]: { field: string; all: number; complex: number }[] } = {
  1: [ // Shani
    { field: 'Cardiology', all: 45, complex: 12 },
    { field: 'Pulmonology', all: 38, complex: 8 },
    { field: 'Gastroenterology', all: 32, complex: 6 },
    { field: 'Nephrology', all: 28, complex: 9 },
    { field: 'Neurology', all: 22, complex: 5 },
    { field: 'Endocrinology', all: 18, complex: 4 },
    { field: 'Infectious Disease', all: 35, complex: 11 },
    { field: 'Hematology', all: 15, complex: 3 },
  ],
  2: [ // Hillel
    { field: 'Cardiology', all: 62, complex: 18 },
    { field: 'Pulmonology', all: 55, complex: 14 },
    { field: 'Gastroenterology', all: 48, complex: 12 },
    { field: 'Nephrology', all: 41, complex: 15 },
    { field: 'Neurology', all: 35, complex: 9 },
    { field: 'Endocrinology', all: 29, complex: 7 },
    { field: 'Infectious Disease', all: 52, complex: 16 },
    { field: 'Hematology', all: 24, complex: 6 },
  ],
  3: [ // Maayan
    { field: 'Cardiology', all: 28, complex: 5 },
    { field: 'Pulmonology', all: 22, complex: 4 },
    { field: 'Gastroenterology', all: 19, complex: 3 },
    { field: 'Nephrology', all: 15, complex: 4 },
    { field: 'Neurology', all: 12, complex: 2 },
    { field: 'Endocrinology', all: 10, complex: 1 },
    { field: 'Infectious Disease', all: 25, complex: 6 },
    { field: 'Hematology', all: 8, complex: 2 },
  ],
  4: [ // Leehi
    { field: 'Cardiology', all: 72, complex: 22 },
    { field: 'Pulmonology', all: 65, complex: 18 },
    { field: 'Gastroenterology', all: 58, complex: 15 },
    { field: 'Nephrology', all: 52, complex: 19 },
    { field: 'Neurology', all: 45, complex: 12 },
    { field: 'Endocrinology', all: 38, complex: 9 },
    { field: 'Infectious Disease', all: 61, complex: 20 },
    { field: 'Hematology', all: 32, complex: 8 },
  ],
  5: [ // Sivan
    { field: 'Cardiology', all: 38, complex: 8 },
    { field: 'Pulmonology', all: 32, complex: 7 },
    { field: 'Gastroenterology', all: 28, complex: 5 },
    { field: 'Nephrology', all: 24, complex: 7 },
    { field: 'Neurology', all: 19, complex: 4 },
    { field: 'Endocrinology', all: 15, complex: 3 },
    { field: 'Infectious Disease', all: 35, complex: 9 },
    { field: 'Hematology', all: 12, complex: 2 },
  ],
  6: [ // Noa
    { field: 'Cardiology', all: 52, complex: 15 },
    { field: 'Pulmonology', all: 48, complex: 12 },
    { field: 'Gastroenterology', all: 42, complex: 10 },
    { field: 'Nephrology', all: 36, complex: 12 },
    { field: 'Neurology', all: 30, complex: 7 },
    { field: 'Endocrinology', all: 25, complex: 6 },
    { field: 'Infectious Disease', all: 46, complex: 14 },
    { field: 'Hematology', all: 21, complex: 5 },
  ],
};

const getHeatColor = (value: number, max: number): string => {
  const intensity = value / max;
  if (intensity < 0.2) return '#EDE9FE';
  if (intensity < 0.4) return '#DDD6FE';
  if (intensity < 0.6) return '#C4B5FD';
  if (intensity < 0.8) return '#A78BFA';
  return '#8B5CF6';
};

// For all patients: compare to department total
// For complex patients: compare to peer average (relative performance)
const getExposureLevel = (
  value: number, 
  reference: number, 
  isComplexMode: boolean
): { level: string; color: string } => {
  if (isComplexMode) {
    // For complex cases, compare against peer average
    // Below 70% of average = Low, 70-130% = Med, Above 130% = High
    const ratio = reference > 0 ? (value / reference) : 0;
    if (ratio < 0.7) return { level: 'Low', color: '#F87171' };
    if (ratio < 1.3) return { level: 'Med', color: '#FBBF24' };
    return { level: 'High', color: '#34D399' };
  } else {
    // For all patients, use department total percentage
    const percentage = (value / reference) * 100;
    if (percentage < 10) return { level: 'Low', color: '#F87171' };
    if (percentage < 20) return { level: 'Med', color: '#FBBF24' };
    return { level: 'High', color: '#34D399' };
  }
};

const getComparisonIndicator = (value: number, avg: number): { symbol: string; color: string } => {
  const diff = ((value - avg) / avg) * 100;
  if (diff > 15) return { symbol: '↑', color: '#10B981' };
  if (diff < -15) return { symbol: '↓', color: '#EF4444' };
  return { symbol: '→', color: '#9CA3AF' };
};

const ClinicalExposureHeatmap: React.FC<ClinicalExposureHeatmapProps> = ({ residentId, globalTimeframe }) => {
  const [showComplexOnly, setShowComplexOnly] = useState(false);
  
  // Map global timeframe to multipliers
  const timeframeMultipliers: { [key: string]: number } = {
    '1 week': 0.25,
    '1 month': 0.5,
    '2 months': 0.75,
    '6 months': 1.0,
  };
  
  const multiplier = globalTimeframe ? timeframeMultipliers[globalTimeframe] : 0.5;
  const data = exposureData[residentId] || exposureData[1];
  
  const adjustedData = data.map(d => ({
    ...d,
    all: Math.round(d.all * multiplier),
    complex: Math.round(d.complex * multiplier),
  }));
  
  const values = adjustedData.map(d => showComplexOnly ? d.complex : d.all);
  const maxValue = Math.max(...values, 1);

  return (
    <div className="heatmap-container">
      <div className="heatmap-header">
        <h3>Clinical Exposure by Medical Field</h3>
        <div className="heatmap-controls">
          <label className="toggle-switch">
            <input
              type="checkbox"
              checked={showComplexOnly}
              onChange={(e) => setShowComplexOnly(e.target.checked)}
            />
            <span className="toggle-slider"></span>
            <span className="toggle-label">Valuable/Complex Cases Only</span>
          </label>
        </div>
      </div>
      
      <div className="heatmap-grid">
        {adjustedData.map((item, index) => {
          const value = showComplexOnly ? item.complex : item.all;
          const deptTotal = Math.round(departmentTotals[item.field] * multiplier);
          const avgValue = showComplexOnly 
            ? Math.round(residentAverages[item.field].complex * multiplier)
            : Math.round(residentAverages[item.field].all * multiplier);
          // For complex mode: compare to peer average; for all: compare to dept total
          const exposureReference = showComplexOnly ? avgValue : deptTotal;
          const exposure = getExposureLevel(value, exposureReference, showComplexOnly);
          const comparison = getComparisonIndicator(value, avgValue);
          
          return (
            <div
              key={index}
              className="heatmap-cell"
              style={{ backgroundColor: getHeatColor(value, maxValue) }}
            >
              <div className="cell-main">
                <div className="cell-value">{value}</div>
                <div className="cell-comparison" style={{ color: comparison.color }}>
                  {comparison.symbol}
                </div>
              </div>
              <div className="cell-label">{item.field}</div>
              <div className="cell-exposure" style={{ backgroundColor: exposure.color }}>
                {exposure.level}
              </div>
            </div>
          );
        })}
      </div>
      
      <div className="heatmap-footer">
        <div className="legend-section">
          <span className="legend-title">Exposure Level:</span>
          <div className="legend-items">
            <span className="legend-item"><span className="legend-dot" style={{backgroundColor: '#F87171'}}></span> Low</span>
            <span className="legend-item"><span className="legend-dot" style={{backgroundColor: '#FBBF24'}}></span> Med</span>
            <span className="legend-item"><span className="legend-dot" style={{backgroundColor: '#34D399'}}></span> High</span>
          </div>
        </div>
        <div className="legend-section">
          <span className="legend-title">vs. Peers:</span>
          <div className="legend-items">
            <span className="legend-item"><span style={{color: '#EF4444'}}>↓</span> Below</span>
            <span className="legend-item"><span style={{color: '#9CA3AF'}}>→</span> Average</span>
            <span className="legend-item"><span style={{color: '#10B981'}}>↑</span> Above</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClinicalExposureHeatmap;
